import { parseApiDate } from '@/lib/dates';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { humaniseEvent, type AuditLogEntry, type AuditLogMeta } from '@/types/auditLogs';

const ALL = 'all';

function formatTimestamp(iso: string): string {
  return parseApiDate(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/** "role: support to admin", or null when neither side carries values. */
function formatChange(entry: AuditLogEntry): string | null {
  const keys = [...new Set([...Object.keys(entry.oldValues ?? {}), ...Object.keys(entry.newValues ?? {})])];
  if (keys.length === 0) {
    return null;
  }

  return keys
    .map((key) => {
      const before = entry.oldValues?.[key];
      const after = entry.newValues?.[key];
      return `${key}: ${before === undefined ? 'not set' : String(before)} to ${
        after === undefined ? 'not set' : String(after)
      }`;
    })
    .join(', ');
}

export function ActivityPage() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [meta, setMeta] = useState<AuditLogMeta | null>(null);
  const [event, setEvent] = useState(ALL);
  const [actor, setActor] = useState(ALL);
  const [page, setPage] = useState(1);
  /**
   * The filter combination currently on screen, and the one the last settled
   * response was for. Deriving isLoading from the two means the flag never has
   * to be switched on from inside the effect.
   */
  const requestKey = JSON.stringify([event, actor, page]);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const isLoading = loadedKey !== requestKey;

  useEffect(() => {
    // `active` is what makes a superseded response harmless: the cleanup runs
    // before the next fetch starts and on unmount, so a slow early response can
    // no longer repaint the table with results the user has already moved past.
    let active = true;

    void (async () => {
      try {
        const params = new URLSearchParams({ page: String(page) });
        if (event !== ALL) {
          params.set('event', event);
        }
        if (actor !== ALL) {
          params.set('actor', actor);
        }
        const response = await api.get(`/audit-logs?${params.toString()}`);
        if (!active) return;
        setEntries(response.data.data);
        setMeta(response.data.meta);
      } catch (error) {
        if (!active) return;
        // Without this the table just stays empty, which reads identically to a
        // deployment where nothing has happened yet.
        setEntries([]);
        setMeta(null);
        toast.error(getApiErrorMessage(error), { id: 'activity-log-error' });
      }
      if (active) setLoadedKey(requestKey);
    })();

    return () => {
      active = false;
    };
  }, [event, actor, page, requestKey]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Activity</h1>
        <p className="text-muted-foreground">
          Every back-office action, newest first. Entries are never edited or removed.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              <Select
                value={event}
                onValueChange={(value) => {
                  setEvent(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[200px]" aria-label="Filter by event">
                  <SelectValue placeholder="All events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All events</SelectItem>
                  {(meta?.events ?? []).map((name) => (
                    <SelectItem key={name} value={name}>
                      {humaniseEvent(name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={actor}
                onValueChange={(value) => {
                  setActor(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[200px]" aria-label="Filter by partner">
                  <SelectValue placeholder="All partners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All partners</SelectItem>
                  {(meta?.actors ?? []).map((option) => (
                    <SelectItem key={option.uuid} value={option.uuid}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {meta && (
              <p className="text-sm text-muted-foreground">
                {meta.total} {meta.total === 1 ? 'entry' : 'entries'}
              </p>
            )}
          </div>

          {entries.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {isLoading ? 'Loading activity.' : 'No activity matches these filters.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Partner</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>What happened</TableHead>
                    <TableHead>Changes</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="whitespace-nowrap">{formatTimestamp(entry.createdAt)}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">
                        {entry.actor?.name ?? 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{humaniseEvent(entry.event)}</Badge>
                      </TableCell>
                      <TableCell>{entry.description}</TableCell>
                      <TableCell className="text-muted-foreground">{formatChange(entry) ?? 'None'}</TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {entry.ipAddress ?? 'Not recorded'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {meta && meta.lastPage > 1 && (
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {meta.currentPage} of {meta.lastPage}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= meta.lastPage}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
